import "./HourlySalesReport.scss";

import React from "react";
import usesFeatures from "usesFeatures";
import moment from "moment";
import Chart from "react-apexcharts";
import { withRouter } from "react-router-dom";

import Header from "Header";
import DatePicker from "DatePicker";
import request from "lib/request";
import { formatCurrency } from "lib/utils/formatCurrency";

class HourlySalesReport extends React.Component {
  state = {
    lines: [
      {
        id: 1,
        startTime: null,
        storeId: "",
        data: [],
        loading: false,
        total: 0,
        orderCount: 0,
      },
    ],
    stores: [],
    linkCopied: false,
    timezoneAbbr: null,
  };

  componentDidMount() {
    this.fetchStores();
    this.loadFiltersFromURL();
  }

  fetchStores = () => {
    request.get("/venue_admin/stores").then(({ data }) => {
      this.setState({ stores: data.stores || [] });
    });
  };

  loadFiltersFromURL = () => {
    const params = new URLSearchParams(this.props.location.search);
    const lines = [];

    for (let i = 1; i <= 3; i++) {
      const date = params.get(`line${i}_date`);
      const storeId = params.get(`line${i}_store`);

      if (date) {
        lines.push({
          id: i,
          startTime: date,
          storeId: storeId || "",
          data: [],
          loading: false,
          total: 0,
          orderCount: 0,
        });
      }
    }

    if (lines.length > 0) {
      this.setState({ lines }, () => {
        lines.forEach((line) => this.fetchLineData(line.id));
      });
    }
  };

  updateURL = () => {
    const params = new URLSearchParams();

    this.state.lines.forEach((line, index) => {
      if (line.startTime) {
        params.set(`line${line.id}_date`, line.startTime);
        if (line.storeId) {
          params.set(`line${line.id}_store`, line.storeId);
        }
      }
    });

    const queryString = params.toString();
    const newUrl = queryString
      ? `${this.props.location.pathname}?${queryString}`
      : this.props.location.pathname;

    this.props.history.replace(newUrl);
  };

  copyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      this.setState({ linkCopied: true });
      setTimeout(() => this.setState({ linkCopied: false }), 2000);
    });
  };

  addLine = () => {
    if (this.state.lines.length >= 3) return;

    const newLine = {
      id: Math.max(...this.state.lines.map((l) => l.id)) + 1,
      startTime: null,
      storeId: "",
      data: [],
      loading: false,
      total: 0,
      orderCount: 0,
    };

    this.setState(
      (prev) => ({
        lines: [...prev.lines, newLine],
      }),
      this.updateURL
    );
  };

  removeLine = (lineId) => {
    if (this.state.lines.length <= 1) return;

    this.setState(
      (prev) => ({
        lines: prev.lines.filter((l) => l.id !== lineId),
      }),
      this.updateURL
    );
  };

  updateLine = (lineId, field, value) => {
    this.setState(
      (prev) => ({
        lines: prev.lines.map((line) =>
          line.id === lineId ? { ...line, [field]: value } : line
        ),
      }),
      () => {
        this.updateURL();
        if (field === "startTime" || field === "storeId") {
          this.fetchLineData(lineId);
        }
      }
    );
  };

  fetchLineData = (lineId) => {
    const line = this.state.lines.find((l) => l.id === lineId);
    if (!line || !line.startTime) return;

    this.setState((prev) => ({
      lines: prev.lines.map((l) =>
        l.id === lineId ? { ...l, loading: true } : l
      ),
    }));

    // Format date as YYYY-MM-DD for API
    const formattedDate = moment(line.startTime).format("YYYY-MM-DD");

    const params = {
      date: formattedDate,
      store_id: line.storeId || null,
    };

    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null))
    ).toString();

    request
      .get(`/venue_admin/hourly_sales?${query}`)
      .then(({ data }) => {
        this.setState((prev) => ({
          lines: prev.lines.map((l) =>
            l.id === lineId
              ? {
                  ...l,
                  data: data.hourly_data || [],
                  total: data.total || 0,
                  orderCount: data.order_count || 0,
                  loading: false,
                }
              : l
          ),
          timezoneAbbr: data.timezone_abbr || prev.timezoneAbbr,
        }));
      })
      .catch(() => {
        this.setState((prev) => ({
          lines: prev.lines.map((l) =>
            l.id === lineId ? { ...l, loading: false } : l
          ),
        }));
      });
  };

  getChartSeries = () => {
    const series = [];

    this.state.lines.forEach((line, index) => {
      if (line.data.length > 0) {
        const lineData = line.data.map((point) => point.sales / 100);

        const storeName = line.storeId
          ? this.state.stores.find((s) => s.id === parseInt(line.storeId))
              ?.name || "Unknown Store"
          : "All Stores";

        const label = `${moment(line.startTime).format("MMM D, YYYY")} - ${storeName}`;

        series.push({
          name: label,
          data: lineData,
        });
      }
    });

    return series;
  };

  getChartOptions = () => {
    return {
      chart: {
        id: "hourly-sales",
        height: 400,
        type: "line",
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: true,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 3,
        curve: "smooth",
      },
      grid: {
        row: {
          colors: ["transparent", "transparent"],
          opacity: 0.5,
        },
      },
      xaxis: {
        categories: Array.from({ length: 24 }, (_, i) => i),
        title: {
          text: "Hour of Day",
        },
        labels: {
          formatter: (val) => `${val}:00`,
        },
      },
      yaxis: {
        title: {
          text: "Sales ($)",
        },
        labels: {
          formatter: (val) => `$${val.toFixed(2)}`,
        },
      },
      tooltip: {
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const line = this.state.lines.filter((l) => l.data.length > 0)[seriesIndex];
          if (line && line.data[dataPointIndex]) {
            const point = line.data[dataPointIndex];
            const sales = formatCurrency(point.sales);
            const seriesName = w.globals.seriesNames[seriesIndex];
            return `
              <div class="apexcharts-tooltip-custom">
                <div class="apexcharts-tooltip-title">${seriesName}</div>
                  <span class="apexcharts-tooltip-text-value">${sales} - ${point.orders} orders</span>
              </div>
            `;
          }
          return "";
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
      },
    };
  };

  renderLineControls = (line, index) => {
    const { stores } = this.state;

    return (
      <div key={line.id} className="Card s-card mb-4">
        <div className="is-flex is-align-items-center mb-3">
          <h3 className="title is-5 mb-0">Line {index + 1}</h3>
          {this.state.lines.length > 1 && (
            <button
              className="button is-small is-danger is-outlined ml-auto"
              onClick={() => this.removeLine(line.id)}
            >
              Remove
            </button>
          )}
        </div>

        <div className="columns">
          <div className="column is-6">
            <label className="label">Date</label>
            <DatePicker
              value={line.startTime}
              input_type="date"
              onChange={(date) => this.updateLine(line.id, "startTime", date)}
              placeholder="Select date"
            />
          </div>

          <div className="column is-6">
            <label className="label">Store</label>
            <select
              className="input"
              value={line.storeId}
              onChange={(e) =>
                this.updateLine(line.id, "storeId", e.target.value)
              }
            >
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {line.loading && (
          <div className="has-text-centered py-3">
            <span className="is-size-6">Loading data...</span>
          </div>
        )}
      </div>
    );
  };

  renderChart = () => {
    const series = this.getChartSeries();

    if (series.length === 0) {
      return (
        <div className="Card s-card has-text-centered py-6">
          <p className="is-size-5 has-text-grey">
            Select a start time for at least one line to view the chart
          </p>
        </div>
      );
    }

    return (
      <div className="Card s-card">
        <h3 className="title is-5 mb-4">
          Hourly Sales Chart{this.state.timezoneAbbr ? ` (${this.state.timezoneAbbr})` : ''}
        </h3>
        <Chart
          type="line"
          height={400}
          series={series}
          options={this.getChartOptions()}
        />
      </div>
    );
  };

  renderTotalsTable = () => {
    const linesWithData = this.state.lines.filter(
      (line) => line.data.length > 0
    );

    if (linesWithData.length === 0) return null;

    return (
      <div className="Card s-card mt-4">
        <h3 className="title is-5 mb-4">Totals by Day</h3>
        <table className="table is-fullwidth is-striped">
          <thead>
            <tr>
              <th>Date</th>
              <th>Store</th>
              <th>Total Sales</th>
              <th>Total Orders</th>
            </tr>
          </thead>
          <tbody>
            {linesWithData.map((line, index) => {
              const storeName = line.storeId
                ? this.state.stores.find((s) => s.id === parseInt(line.storeId))
                    ?.name || "Unknown Store"
                : "All Stores";

              return (
                <tr key={line.id}>
                  <td>{moment(line.startTime).format("MMM D, YYYY")}</td>
                  <td>{storeName}</td>
                  <td>{formatCurrency(line.total)}</td>
                  <td>{line.orderCount}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  render() {
    return (
      <div className="HourlySalesReport">
        <Header text="Hourly Sales Report" actions={true}>
          {this.state.lines.length < 3 && (
            <button className="button is-primary" onClick={this.addLine}>
              Add Line
            </button>
          )}
        </Header>

        <div className="mb-4">
          {this.state.lines.map((line, index) =>
            this.renderLineControls(line, index)
          )}
        </div>

        {this.renderChart()}
        {this.renderTotalsTable()}
      </div>
    );
  }
}

export default withRouter(usesFeatures(["data"], HourlySalesReport));
